import { useState } from 'react';
import { FileText } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';

type Language = 'en' | 'fr' | 'ar';

const translations = {
    en: {
        title: 'Terms of Service',
        lastUpdated: 'Last updated: February 2026',
        backToHome: 'Back to Home',
        sections: [
            {
                title: '1. Acceptance of Terms',
                content: 'By accessing and using karia.tn, you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service govern your use of the karia.tn platform and all related services.'
            },
            {
                title: '2. Description of Service',
                content: 'karia.tn is a comprehensive ERP solution designed for car rental agencies. The service includes vehicle management, booking systems, client management, employee management, and analytics tools. We reserve the right to modify or discontinue the service at any time.'
            },
            {
                title: '3. User Accounts and Registration',
                content: 'To access certain features, you must register for an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You must provide accurate and complete information during registration.'
            },
            {
                title: '4. Acceptable Use',
                content: 'You agree not to use the service for any unlawful purpose or in any way that could damage, disable, overburden, or impair the service. You may not attempt to gain unauthorized access to any part of the service, other accounts, or computer systems.'
            },
            {
                title: '5. Payment and Billing',
                content: 'Paid services are billed in advance on a monthly basis. All fees are non-refundable unless otherwise specified. We reserve the right to change pricing at any time with 30 days notice to existing customers.'
            },
            {
                title: '6. Data and Privacy',
                content: 'Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information. You retain ownership of your data, and we will not access it except as necessary to provide the service.'
            },
            {
                title: '7. Intellectual Property',
                content: 'The service and its original content, features, and functionality are and will remain the exclusive property of karia.tn and its licensors. The service is protected by copyright, trademark, and other laws.'
            },
            {
                title: '8. Limitation of Liability',
                content: 'In no event shall karia.tn be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.'
            },
            {
                title: '9. Termination',
                content: 'We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the service will cease immediately.'
            },
            {
                title: '10. Changes to Terms',
                content: 'We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.'
            },
            {
                title: '11. Contact Information',
                content: 'If you have any questions about these Terms, please contact us at legal@karia.tn or through our website contact form.'
            }
        ]
    },
    fr: {
        title: 'Conditions d\'Utilisation',
        lastUpdated: 'Dernière mise à jour : Février 2026',
        backToHome: 'Retour à l\'accueil',
        sections: [
            {
                title: '1. Acceptation des Conditions',
                content: 'En accédant et en utilisant karia.tn, vous acceptez et convenez d\'être lié par les termes et dispositions de cet accord. Ces Conditions d\'Utilisation régissent votre utilisation de la plateforme karia.tn et tous les services connexes.'
            },
            {
                title: '2. Description du Service',
                content: 'karia.tn est une solution ERP complète conçue pour les agences de location de voitures. Le service comprend la gestion des véhicules, les systèmes de réservation, la gestion clientèle, la gestion des employés et les outils d\'analyse. Nous nous réservons le droit de modifier ou d\'interrompre le service à tout moment.'
            },
            {
                title: '3. Comptes Utilisateur et Inscription',
                content: 'Pour accéder à certaines fonctionnalités, vous devez vous inscrire pour un compte. Vous êtes responsable du maintien de la confidentialité des informations de votre compte et de toutes les activités qui se produisent sous votre compte. Vous devez fournir des informations précises et complètes lors de l\'inscription.'
            },
            {
                title: '4. Utilisation Acceptable',
                content: 'Vous acceptez de ne pas utiliser le service à des fins illégales ou de manière à endommager, désactiver, surcharger ou altérer le service. Vous ne pouvez pas tenter d\'obtenir un accès non autorisé à toute partie du service, à d\'autres comptes ou à des systèmes informatiques.'
            },
            {
                title: '5. Paiement et Facturation',
                content: 'Les services payants sont facturés à l\'avance sur une base mensuelle. Tous les frais ne sont pas remboursables sauf indication contraire. Nous nous réservons le droit de modifier les prix à tout moment avec un préavis de 30 jours aux clients existants.'
            },
            {
                title: '6. Données et Confidentialité',
                content: 'Votre confidentialité est importante pour nous. Veuillez consulter notre Politique de Confidentialité pour comprendre comment nous collectons, utilisons et protégeons vos informations. Vous conservez la propriété de vos données, et nous n\'y accéderons que si nécessaire pour fournir le service.'
            },
            {
                title: '7. Propriété Intellectuelle',
                content: 'Le service et son contenu original, ses fonctionnalités et sa fonctionnalité sont et resteront la propriété exclusive de karia.tn et de ses concédants de licence. Le service est protégé par le droit d\'auteur, les marques de commerce et d\'autres lois.'
            },
            {
                title: '8. Limitation de Responsabilité',
                content: 'En aucun cas karia.tn ne sera responsable de tout dommage indirect, accidentel, spécial, consécutif ou punitif, y compris sans limitation, la perte de profits, de données, d\'utilisation, de clientèle ou d\'autres pertes intangibles.'
            },
            {
                title: '9. Résiliation',
                content: 'Nous pouvons résilier ou suspendre votre compte immédiatement, sans préavis ni responsabilité, pour quelque raison que ce soit, y compris sans limitation si vous violez les Conditions. Lors de la résiliation, votre droit d\'utiliser le service cessera immédiatement.'
            },
            {
                title: '10. Modifications des Conditions',
                content: 'Nous nous réservons le droit, à notre seule discrétion, de modifier ou remplacer ces Conditions à tout moment. Si une révision est importante, nous essaierons de fournir un préavis d\'au moins 30 jours avant l\'entrée en vigueur de nouvelles conditions.'
            },
            {
                title: '11. Informations de Contact',
                content: 'Si vous avez des questions sur ces Conditions, veuillez nous contacter à legal@karia.tn ou via notre formulaire de contact sur le site web.'
            }
        ]
    },
    ar: {
        title: 'شروط الخدمة',
        lastUpdated: 'آخر تحديث: فبراير 2026',
        backToHome: 'العودة للرئيسية',
        sections: [
            {
                title: '1. قبول الشروط',
                content: 'من خلال الوصول واستخدام karia.tn، أنت تقبل وتوافق على الالتزام بشروط وأحكام هذه الاتفاقية. تحكم شروط الخدمة هذه استخدامك لمنصة karia.tn وجميع الخدمات ذات الصلة.'
            },
            {
                title: '2. وصف الخدمة',
                content: 'karia.tn هو حل ERP شامل مصمم لوكالات تأجير السيارات. تتضمن الخدمة إدارة المركبات وأنظمة الحجز وإدارة العملاء وإدارة الموظفين وأدوات التحليل. نحتفظ بالحق في تعديل أو إيقاف الخدمة في أي وقت.'
            },
            {
                title: '3. حسابات المستخدمين والتسجيل',
                content: 'للوصول إلى ميزات معينة، يجب عليك التسجيل للحصول على حساب. أنت مسؤول عن الحفاظ على سرية معلومات حسابك وعن جميع الأنشطة التي تحدث تحت حسابك. يجب عليك تقديم معلومات دقيقة وكاملة أثناء التسجيل.'
            },
            {
                title: '4. الاستخدام المقبول',
                content: 'توافق على عدم استخدام الخدمة لأي غرض غير قانوني أو بأي طريقة يمكن أن تضر أو تعطل أو تحمّل الخدمة فوق طاقتها أو تضعفها. لا يجوز لك محاولة الحصول على وصول غير مصرح به إلى أي جزء من الخدمة أو حسابات أخرى أو أنظمة الحاسوب.'
            },
            {
                title: '5. الدفع والفوترة',
                content: 'يتم فوترة الخدمات المدفوعة مقدماً على أساس شهري. جميع الرسوم غير قابلة للاسترداد ما لم يُنص على خلاف ذلك. نحتفظ بالحق في تغيير الأسعار في أي وقت مع إشعار مسبق للعملاء الحاليين لمدة 30 يوماً.'
            },
            {
                title: '6. البيانات والخصوصية',
                content: 'خصوصيتك مهمة بالنسبة لنا. يرجى مراجعة سياسة الخصوصية الخاصة بنا لفهم كيف نجمع ونستخدم ونحمي معلوماتك. تحتفظ بملكية بياناتك، ولن نصل إليها إلا حسب الضرورة لتقديم الخدمة.'
            },
            {
                title: '7. الملكية الفكرية',
                content: 'الخدمة ومحتواها الأصلي وميزاتها ووظائفها هي وستبقى الملكية الحصرية لـ karia.tn ومرخصيها. الخدمة محمية بحقوق الطبع والنشر والعلامات التجارية والقوانين الأخرى.'
            },
            {
                title: '8. تحديد المسؤولية',
                content: 'في أي حال من الأحوال لن تكون karia.tn مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية، بما في ذلك دون حصر، فقدان الأرباح أو البيانات أو الاستخدام أو الشهرة أو الخسائر غير الملموسة الأخرى.'
            },
            {
                title: '9. الإنهاء',
                content: 'يجوز لنا إنهاء أو تعليق حسابك فوراً، دون إشعار مسبق أو مسؤولية، لأي سبب كان، بما في ذلك دون حصر إذا انتهكت الشروط. عند الإنهاء، سينتهي حقك في استخدام الخدمة فوراً.'
            },
            {
                title: '10. تغييرات الشروط',
                content: 'نحتفظ بالحق، وفق تقديرنا الوحيد، في تعديل أو استبدال هذه الشروط في أي وقت. إذا كان التنقيح جوهرياً، سنحاول تقديم إشعار مسبق بـ 30 يوماً على الأقل قبل دخول أي شروط جديدة حيز التنفيذ.'
            },
            {
                title: '11. معلومات الاتصال',
                content: 'إذا كان لديك أي أسئلة حول هذه الشروط، يرجى الاتصال بنا على legal@karia.tn أو من خلال نموذج الاتصال على موقعنا.'
            }
        ]
    }
};

const Terms = () => {
    const [lang, setLang] = useState<Language>('fr'); // Default to French
    const t = translations[lang];
    const isRTL = lang === 'ar';

    return (
        <div className="min-h-screen bg-white text-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
            <Navbar currentLang={lang} onLanguageChange={setLang} />

            {/* Hero Section */}
            <section className="bg-slate-50 border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-6 py-12 text-center">
                    <div className="flex justify-center mb-4">
                        <div className="p-3 bg-blue-100 rounded-xl">
                            <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">{t.title}</h1>
                    <p className="text-sm text-slate-500">{t.lastUpdated}</p>
                </div>
            </section>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-6 py-12">
                <div className="prose prose-slate max-w-none" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="space-y-8">
                        {t.sections.map((section, index) => (
                            <section key={index} className="border-b border-slate-100 pb-6 last:border-b-0">
                                <h2 className="text-lg font-semibold text-slate-900 mb-3">{section.title}</h2>
                                <p className="text-slate-700 leading-relaxed">{section.content}</p>
                            </section>
                        ))}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-slate-200 bg-slate-50 mt-16">
                <div className="max-w-4xl mx-auto px-6 py-8 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-600 mb-4">
                        <span>© 2026 karia.tn. Tous droits réservés.</span>
                    </div>
                    <div className="flex items-center justify-center gap-6 text-sm">
                        <a href="/" className="text-slate-600 hover:text-slate-900 transition-colors">{t.backToHome}</a>
                        <a href="/privacy" className="text-slate-600 hover:text-slate-900 transition-colors">Confidentialité</a>
                        <a href="/security" className="text-slate-600 hover:text-slate-900 transition-colors">Sécurité</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Terms;